// Creative caption styles configuration
export const CREATIVE_STYLES = {
  AESTHETIC_MINIMAL: 'aesthetic-minimal',
  PINTEREST_DREAMY: 'pinterest-dreamy',
  MAIN_CHARACTER_ENERGY: 'main-character-energy',
  QUIETLY_POWERFUL: 'quietly-powerful',
  COZY_CHAOS: 'cozy-chaos',
  SOFT_FLEX: 'soft-flex',
  DEEP_REFLECTIVE: 'deep-reflective',
  WITTY_ONE_LINER: 'witty-one-liner',
  POETIC_VISUAL: 'poetic-visual',
  LATE_NIGHT_THOUGHTS: 'late-night-thoughts',
  GEN_Z_CHILL: 'gen-z-chill',
  PROFESSIONAL_LINKEDIN: 'professional-linkedin',
} as const

export const STYLE_METADATA = {
  [CREATIVE_STYLES.AESTHETIC_MINIMAL]: {
    name: 'Aesthetic Minimal',
    description: 'Clean, sophisticated, less-is-more approach with strategic white space',
    icon: '✨',
    color: 'from-gray-100 to-gray-200',
  },
  [CREATIVE_STYLES.PINTEREST_DREAMY]: {
    name: 'Pinterest Dreamy',
    description: 'Soft, dreamy, aspirational captions with Pinterest aesthetic vibes',
    icon: '🌸',
    color: 'from-pink-100 to-purple-200',
  },
  [CREATIVE_STYLES.MAIN_CHARACTER_ENERGY]: {
    name: 'Main Character Energy',
    description: 'Confident, bold statements that position the subject as the focal point',
    icon: '👑',
    color: 'from-yellow-100 to-orange-200',
  },
  [CREATIVE_STYLES.QUIETLY_POWERFUL]: {
    name: 'Quietly Powerful',
    description: 'Understated strength, subtle confidence, impactful without being loud',
    icon: '🌊',
    color: 'from-blue-100 to-indigo-200',
  },
  [CREATIVE_STYLES.COZY_CHAOS]: {
    name: 'Cozy Chaos',
    description: 'Warm, relatable, beautifully imperfect moments with authentic charm',
    icon: '🏠',
    color: 'from-amber-100 to-orange-200',
  },
  [CREATIVE_STYLES.SOFT_FLEX]: {
    name: 'Soft Flex',
    description: 'Gentle confidence, humble bragging with grace and authenticity',
    icon: '💫',
    color: 'from-pink-100 to-rose-200',
  },
  [CREATIVE_STYLES.DEEP_REFLECTIVE]: {
    name: 'Deep & Reflective',
    description: 'Thoughtful, introspective content that invites contemplation',
    icon: '🌙',
    color: 'from-purple-100 to-indigo-200',
  },
  [CREATIVE_STYLES.WITTY_ONE_LINER]: {
    name: 'Witty One-Liner',
    description: 'Clever, concise humor that\'s memorable and shareable',
    icon: '😄',
    color: 'from-green-100 to-emerald-200',
  },
  [CREATIVE_STYLES.POETIC_VISUAL]: {
    name: 'Poetic Visual',
    description: 'Artistic, metaphorical language that elevates the visual experience',
    icon: '🎨',
    color: 'from-violet-100 to-purple-200',
  },
  [CREATIVE_STYLES.LATE_NIGHT_THOUGHTS]: {
    name: 'Late-Night Thoughts',
    description: 'Stream-of-consciousness, vulnerable, authentic musings',
    icon: '🌃',
    color: 'from-slate-100 to-gray-200',
  },
  [CREATIVE_STYLES.GEN_Z_CHILL]: {
    name: 'Gen-Z Chill',
    description: 'Current, casual, culturally aware without trying too hard',
    icon: '😎',
    color: 'from-cyan-100 to-blue-200',
  },
  [CREATIVE_STYLES.PROFESSIONAL_LINKEDIN]: {
    name: 'Professional LinkedIn',
    description: 'Career-focused, achievement-oriented content perfect for professional networking',
    icon: '💼',
    color: 'from-blue-100 to-indigo-200',
  },
} as const

// Platform configuration
export const PLATFORMS = {
  INSTAGRAM: 'instagram',
  LINKEDIN: 'linkedin',
  GENERAL: 'general',
} as const

export const PLATFORM_METADATA = {
  [PLATFORMS.INSTAGRAM]: {
    name: 'Instagram',
    characterLimit: 2200,
    optimalLength: [50, 150],
    icon: '📸',
    color: 'from-pink-500 to-purple-600',
  },
  [PLATFORMS.LINKEDIN]: {
    name: 'LinkedIn',
    characterLimit: 3000,
    optimalLength: [100, 300],
    icon: '💼',
    color: 'from-blue-600 to-blue-700',
  },
  [PLATFORMS.GENERAL]: {
    name: 'General',
    characterLimit: 1000,
    optimalLength: [50, 200],
    icon: '🌐',
    color: 'from-gray-500 to-gray-600',
  },
} as const

// Image processing constants
export const IMAGE_CONSTRAINTS = {
  MAX_FILE_SIZE_MB: 10,
  SUPPORTED_FORMATS: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/avif', 'image/tiff', 'image/tif'],
  SUPPORTED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.avif', '.tiff', '.tif'],
  MIN_DIMENSIONS: { width: 100, height: 100 },
  MAX_DIMENSIONS: { width: 4096, height: 4096 },
} as const

// Generation settings
export const GENERATION_SETTINGS = {
  VARIATIONS_PER_STYLE: 3,
  MAX_CONCURRENT_STYLES: 5,
  TIMEOUT_MS: 30000,
  RETRY_ATTEMPTS: 3,
  QUALITY_THRESHOLD: 0.7,
} as const

// UI constants
export const UI_CONSTANTS = {
  ANIMATION_DURATION: 300,
  DEBOUNCE_DELAY: 500,
  TOAST_DURATION: 4000,
  PROGRESS_UPDATE_INTERVAL: 100,
} as const

// Error messages
export const ERROR_MESSAGES = {
  UNSUPPORTED_FORMAT: 'Please upload a JPEG, PNG, WebP, HEIC, AVIF, or TIFF image file.',
  FILE_TOO_LARGE: 'Image file is too large. Please choose a file under 10MB.',
  UPLOAD_FAILED: 'Failed to upload image. Please try again.',
  GENERATION_FAILED: 'Failed to generate captions. Please try again.',
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  INVALID_IMAGE: 'Invalid image file. Please choose a different image.',
  PROCESSING_TIMEOUT: 'Image processing is taking longer than expected. Please try again.',
} as const